/**
 * SmartInspect Assessment Detail Workspace (shell) — mirrors the SmartEvaluate
 * assessment detail architecture: header with meta + status badge, a tab bar
 * driven by the ?tab= query param (no page refresh), and read-only/finalized
 * banners. Tab contents are placeholders for this sprint. Access is gated by
 * the shared application-access logic (hasApp).
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle2, History } from "lucide-react";
import { useConsultant } from "./ConsultantLayout";
import { hasApp, APP_SMARTINSPECT } from "../lib/applications";
import { siGetAssessment, siGetVersion } from "../lib/smartInspectApi";
import SmartInspectClientInputs from "./SmartInspectClientInputs";
import SmartInspectRoiResultsTab from "./SmartInspectRoiResultsTab";
import SmartInspectExecutiveReportTab from "./SmartInspectExecutiveReportTab";
import { SmartInspectVersionHistoryDrawer } from "./SmartInspectVersionHistoryDrawer";

const STATUS = {
  draft: { label: "Draft", cls: "bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/20" },
  inputs_complete: { label: "Completed", cls: "bg-[#ECFDF5] text-[#047857] border border-[#047857]/20" },
  finalized: { label: "Finalized", cls: "bg-[#EFF6FF] text-[#025979] border border-[#025979]/20" },
  demo_requested: { label: "Demo Requested", cls: "bg-[#EDE9FE] text-[#5B21B6] border border-[#5B21B6]/20" },
  archived: { label: "Archived", cls: "bg-[#F3F4F6] text-[#6B7280] border border-[#6B7280]/20" },
};
const statusMeta = (s) => STATUS[s] || { label: s || "—", cls: "bg-[#F3F4F6] text-[#6B7280] border border-[#6B7280]/20" };

const TABS = [
  { key: "client_inputs", label: "Client Inputs", body: "This page will display all customer questionnaire responses." },
  { key: "roi_results", label: "ROI Results", body: "This page will display the SmartInspect ROI calculations." },
  { key: "executive_report", label: "Executive Report", body: "This page will display the consultant-ready executive business case." },
];
const TAB_KEYS = TABS.map((t) => t.key);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

function MetaItem({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-sm text-[#111827] font-medium">{value || "—"}</div>
    </div>
  );
}

export default function SmartInspectAssessmentDetailPage() {
  const user = useConsultant();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  const [viewingVersion, setViewingVersion] = useState(null);

  const authorized = user && hasApp(user, APP_SMARTINSPECT);
  const tabParam = searchParams.get("tab");
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : "client_inputs";

  useEffect(() => {
    if (user && !authorized) navigate("/transformation", { replace: true });
  }, [user, authorized, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await siGetAssessment(id);
      setData(d);
    } catch (e) {
      toast.error("Failed to load assessment");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (authorized) load(); }, [authorized, load]);

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const handleViewVersion = async (vid) => {
    try {
      const v = await siGetVersion(id, vid);
      setViewingVersion(v);
      setTab("executive_report");
    } catch {
      toast.error("Failed to load version");
    }
  };

  const handleVersionChanged = async () => {
    setViewingVersion(null);
    setVersionsRefreshKey((k) => k + 1);
    await load();
  };

  if (!user || !authorized) return null;

  const ans = data?.answers || {};
  const sm = statusMeta(data?.status);
  const isArchived = data?.status === "archived";
  const isFinalized = data?.status === "finalized" || data?.is_finalized;
  const isCompleted = data?.status === "inputs_complete";

  return (
    <div data-testid="smartinspect-assessment-detail">
      <button
        data-testid="si-detail-back"
        onClick={() => navigate("/consultant/smartinspect")}
        className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#111827] mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to SmartInspect Dashboard
      </button>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="si-detail-loading">Loading assessment…</div>
      ) : !data ? (
        <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="si-detail-notfound">Assessment not found.</div>
      ) : (
        <>
          {/* Read-only / finalized banners */}
          {isArchived && (
            <div data-testid="si-detail-readonly-banner" className="mb-4 flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2.5 text-sm text-[#6B7280]">
              <Lock className="w-4 h-4" /> This assessment is archived and read-only.
            </div>
          )}
          {isCompleted && !isArchived && (
            <div data-testid="si-detail-completed-banner" className="mb-4 flex items-center gap-2 rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] px-4 py-2.5 text-sm text-[#047857]">
              <CheckCircle2 className="w-4 h-4" /> Inputs complete — ROI business case is ready to review.
            </div>
          )}
          {isFinalized && !isArchived && (
            <div data-testid="si-detail-finalized-banner" className="mb-4 flex items-center gap-2 rounded-lg border border-[#025979]/20 bg-[#EFF6FF] px-4 py-2.5 text-sm text-[#025979]">
              <CheckCircle2 className="w-4 h-4" /> Executive Report finalized (v{data?.report_version || data?.finalized_version_number || 1}). Editing Client Inputs will reopen it as a draft.
            </div>
          )}

          {/* Assessment header */}
          <div data-testid="si-detail-header" className="rounded-xl border border-[#E5E7EB] bg-white p-5 mb-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-xl font-bold text-[#111827]">
                  {ans.company_name || "SmartInspect Assessment"} — SmartInspect ROI Assessment
                </h1>
              </div>
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${sm.cls}`} data-testid="si-detail-status">{sm.label}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
              <MetaItem label="Company" value={ans.company_name} />
              <MetaItem label="Contact" value={ans.contact_name} />
              <MetaItem label="Package" value={ans.selected_package_name} />
              <MetaItem label="Status" value={sm.label} />
              <MetaItem label="Last Updated" value={fmtDate(data.updated_at)} />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                data-testid="si-version-history-btn"
                onClick={() => setVersionsOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm text-[#025979] hover:text-[#03738C] font-medium border border-[#025979]/30 rounded-md px-3 py-1.5"
              >
                <History className="w-4 h-4" /> Version History
              </button>
            </div>
          </div>

          {/* Tab bar */}
          <div className="border-b border-[#E5E7EB] mb-5" data-testid="si-detail-tabs">
            <div className="flex flex-wrap gap-1">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`si-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === t.key
                      ? "border-[#025979] text-[#025979]"
                      : "border-transparent text-[#6B7280] hover:text-[#111827]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          {activeTab === "client_inputs" ? (
            <div data-testid={`si-tab-panel-${activeTab}`}>
              <SmartInspectClientInputs
                assessment={data}
                readOnly={isArchived}
                onSaved={(updated) => setData((prev) => ({ ...prev, ...updated }))}
              />
            </div>
          ) : activeTab === "roi_results" ? (
            <div data-testid={`si-tab-panel-${activeTab}`}>
              <SmartInspectRoiResultsTab
                id={id}
                assessment={data}
                readOnly={isArchived}
                onFinalized={(updated) => { if (updated) setData((prev) => ({ ...prev, ...updated })); else load(); }}
                onGotoReport={() => setTab("executive_report")}
              />
            </div>
          ) : (
            <div data-testid={`si-tab-panel-${activeTab}`}>
              <SmartInspectExecutiveReportTab
                assessment={data}
                viewingVersion={viewingVersion}
                onExitVersion={() => setViewingVersion(null)}
                onGotoResults={() => setTab("roi_results")}
              />
            </div>
          )}
        </>
      )}

      <SmartInspectVersionHistoryDrawer
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        assessmentId={id}
        refreshKey={versionsRefreshKey}
        onViewVersion={handleViewVersion}
        onVersionChanged={handleVersionChanged}
      />
    </div>
  );
}
